"use client";

import { createContext, useContext, useMemo, useState, useEffect } from "react";
import { DEFAULT_LANG, LANG_KEY, dict, readLang, type Lang } from "@/lib/i18n";

type TFn = {
  (key: {
    [K in keyof typeof dict.en]: (typeof dict.en)[K] extends string ? K : never;
  }[keyof typeof dict.en]): string;
  guests: (n: number) => string;
  nights: (n: number) => string;
  remainingNote: (amt: string) => string;
  stayEnded: (title: string) => string;
  rooms: (rooms: number, beds: number, guests: number) => string;
  roomsBaths: (rooms: number, baths: number) => string;
  reviewsCount: (n: number) => string;
  weekdays: string[];
};

const LangContext = createContext<{ lang: Lang; setLang: (l: Lang) => void; t: TFn } | null>(null);

export function LangProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(DEFAULT_LANG);

  useEffect(() => {
    setLangState(readLang(localStorage.getItem(LANG_KEY)));
  }, []);

  function setLang(next: Lang) {
    setLangState(next);
    localStorage.setItem(LANG_KEY, next);
    document.documentElement.lang = next === "ko" ? "ko" : "en";
  }

  const value = useMemo(() => {
    const pack = dict[lang];
    const t = ((key: keyof typeof dict.en) => {
      const v = pack[key];
      return typeof v === "string" ? v : String(v);
    }) as TFn;
    t.guests = pack.guestCount;
    t.nights = pack.nightsCount;
    t.remainingNote = pack.remainingNote;
    t.stayEnded = pack.stayEnded;
    t.rooms = pack.roomsBedsGuests;
    t.roomsBaths = pack.roomsBaths;
    t.reviewsCount = pack.reviewsCount;
    t.weekdays = pack.weekdaysLong;
    return { lang, setLang, t };
  }, [lang]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang outside provider");
  return ctx;
}
